/**
 * `<enova-carousel>` — el riel horizontal compartido del tema, sobre Embla.
 *
 * QUÉ HACE EL CSS Y QUÉ HACE EMBLA:
 *
 * - El CSS es el carrusel BASE: `overflow-x: auto` + `scroll-snap` en el viewport y el
 *   ancho por preset en el slide (`assets/home.css`). Eso ya da arrastre táctil,
 *   inercia, snap, rueda y teclado sin JS (`e2e/tests/no-js.spec.js`).
 * - Embla entra encima cuando el riel se acerca al viewport Y el puntero es fino:
 *   física de arrastre con ratón, `slidesToScroll`, índice de snap para flechas y
 *   dots. `core/embla` pone `is-enhanced` y el CSS apaga el scroll nativo. Embla no
 *   reparenta: el flex y el `gap` del tema se quedan.
 * - CON EL DEDO (`pointer: coarse`) NO HAY EMBLA: el carrusel es el scroller CSS, como
 *   en mammut.com. Aquí solo queda el estado de las flechas que sigan visibles
 *   (anuncios, guía de tallas) leído de `scrollLeft`, y `carousel--ready`. Con el dedo
 *   TODOS los rieles envuelven —también los de productos, que con ratón no—: el gesto
 *   pide continuidad y no hay flechas que apagar. `bucleNativo` clona un colchón de
 *   slides a cada lado y recoloca al asentar; `data-carousel-loop-touch="false"` lo
 *   apaga. Si el puntero cambia (DevTools, híbridos) se cambia de motor en caliente.
 *
 * LOS CONTROLES SIGUEN SIENDO DEL TEMA. Las flechas se centran sobre la FOTO
 * (`--carousel-media-h`).
 *
 * Opciones: `data-embla-*`. Cinco markups montan este elemento y comparten `.carousel__*`.
 */

import {
  cargarEmbla,
  cargarClassNames,
  opcionesDesde,
  montar,
  instante,
  alVer,
  precalentar,
  rielNativo,
  vigilarRielNativo,
  bucleNativo,
} from "core/embla";

class EnovaCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #viewport = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #prev = null;
  /** @type {HTMLElement | null} */
  #next = null;
  /** @type {HTMLElement | null} */
  #dotsHost = null;
  /** @type {HTMLButtonElement[]} */
  #dots = [];
  /** @type {any} instancia de Embla, `null` hasta que el riel se asoma */
  #embla = null;
  /** @type {(() => void) | null} */
  #cancelarGate = null;
  /** @type {ResizeObserver | null} */
  #resize = null;
  #rafMedida = 0;
  #desconectado = false;
  /**
   * Se renueva en cada conexión y se aborta al desconectar.
   *
   * Un custom element puede desconectarse y volver a conectarse: el carrito y la PLP
   * reemplazan fragmentos enteros, y `motion/cart` mueve nodos para animar. Sin esto, cada
   * reconexión sumaba otro listener a las flechas —dos pasos por clic— y el `#desconectado`
   * que quedaba en `true` impedía volver a montar Embla.
   * @type {AbortController | null}
   */
  #abort = null;
  /** Hay un `#activar()` en vuelo: evita montar dos veces si el gate y `refresh()` coinciden. */
  #activando = false;
  /**
   * Un `AbortController` POR MONTAJE, además del de conexión: `atarTeclado`/`atarRueda`
   * escuchan con `signal`, y al desmontar por cambio de puntero (no por desconexión)
   * quedarían vivos sobre una api destruida.
   * @type {AbortController | null}
   */
  #montaje = null;
  /** @type {(() => void) | null} */
  #cancelarPuntero = null;
  /** @type {ReturnType<typeof bucleNativo> | null} bucle del scroller CSS (dedo) */
  #bucle = null;
  /**
   * «Siguiente» en el último vuelve al primero. Lo pide el bucle (teselas,
   * anuncios). En rieles de productos queda en `false` y las flechas se apagan.
   */
  #wrap = true;

  connectedCallback() {
    this.#desconectado = false;
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#viewport = this.querySelector("[data-carousel]");
    if (!this.#viewport) return;

    this.#track = this.#viewport.querySelector(".carousel__track");
    if (!this.#track) return;

    this.#prev = this.querySelector("[data-carousel-prev]");
    this.#next = this.querySelector("[data-carousel-next]");
    this.#dotsHost = this.querySelector("[data-carousel-dots]");

    this.#prev?.addEventListener("click", (e) => this.#alClickFlecha(e, -1), { signal });
    this.#next?.addEventListener("click", (e) => this.#alClickFlecha(e, 1), { signal });

    // El alto de la foto se publica desde el primer fotograma, antes de que Embla
    // llegue: las flechas ya están colocadas cuando se hacen visibles.
    this.#medirFoto();

    if (this.classList.contains("contenido-personalizado__slider")) {
      if ("ResizeObserver" in window) {
        this.#resize = new ResizeObserver(() => this.#encolarMedida());
        this.#resize.observe(this.#viewport);
      }
      precalentar();
      this.#montarArrastreRaton(signal);
      this.#montarCpSlider(signal);
      return;
    }

    if ("ResizeObserver" in window) {
      this.#resize = new ResizeObserver(() => {
        this.#encolarMedida();
        if (!this.#embla) {
          this.#sincronizarBucle();
          this.#sincronizar();
        }
      });
      this.#resize.observe(this.#viewport);
    }

    this.#wrap = this.#leerBucle();

    // Con el dedo el viewport SÍ se desplaza: las flechas que sigan a la vista se
    // sincronizan con el scroll. Con Embla montado el viewport no scrollea (clip).
    this.#viewport.addEventListener(
      "scroll",
      () => {
        if (!this.#embla) this.#sincronizar();
      },
      { signal, passive: true },
    );
    const track = this.#track;
    this.#bucle = bucleNativo({
      viewport: this.#viewport,
      pista: track,
      slides: () => [...track.querySelectorAll(":scope > .carousel__slide:not([data-carousel-clon])")],
      marcarClon: (clon, vecino) => {
        // El clon no es un slide para nadie más: fuera del lector, fuera del tabulador,
        // sin ids duplicados. Sigue siendo lo que era (un enlace, una tesela) para el tap.
        clon.setAttribute("data-carousel-clon", "");
        clon.setAttribute("aria-hidden", "true");
        clon.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
        clon.querySelectorAll("a, button, [tabindex]").forEach((el) => {
          el.tabIndex = -1;
        });
        if (vecino) {
          clon.querySelectorAll("img").forEach((img) => {
            img.loading = "eager";
          });
        }
      },
      alAsentar: () => this.#sincronizar(),
      colchon: "minimo",
      signal,
    });
    this.#cancelarPuntero = vigilarRielNativo((nativo) => this.#cambiarMotor(nativo));

    // La librería la adelanta el boot; el constructor sigue siendo por riel y
    // por viewport. `precalentar()` aquí cubre el caso de un riel que aparece
    // después (editor, fragmento AJAX) sin pasar otra vez por el boot. Con el dedo
    // no se pide: no se va a usar.
    if (!rielNativo()) precalentar();
    this.#armarGate();
  }

  disconnectedCallback() {
    this.#desconectado = true;
    this.#abort?.abort();
    this.#cancelarGate?.();
    this.#cancelarPuntero?.();
    this.#resize?.disconnect();
    if (this.#rafMedida) cancelAnimationFrame(this.#rafMedida);
    this.#desmontar();
  }


  #destruirEmbla() {
    this.#embla?.destroy();
    this.#embla = null;
    this.#activando = false;
    this.classList.remove("carousel--ready");
  }
  #montarCpSlider(signal) {
    const mq = window.matchMedia("(min-width: 750px)");
    const defer = this.dataset.carouselDefer;

    const visibleSegunDefer = () => {
      if (defer === "mobile") return !mq.matches;
      if (defer === "desktop") return mq.matches;
      return true;
    };

    const sync = () => {
      if (!visibleSegunDefer() || !this.#esMontable()) return;

      if (!mq.matches) {
        this.#cancelarGate?.();
        this.#destruirEmbla();
        this.classList.add("carousel--ready");
        return;
      }

      this.classList.remove("carousel--ready");
      if (this.#embla) {
        this.refresh();
        return;
      }
      void this.#activar();
    };

    mq.addEventListener("change", sync, { signal });
    this.#observarMontaje(sync, signal);
    this.#cancelarGate = alVer(this, sync);
    sync();
  }
  #esMontable() {
    if (!this.isConnected || !this.#viewport) return false;

    const scope = this.closest("[class*='--mobile-only'], [class*='--desktop-only']") || this;
    if (getComputedStyle(scope).display === "none") return false;
    if (getComputedStyle(this).display === "none") return false;
    return this.#viewport.clientWidth > 0;
  }
  #observarMontaje(fn, signal) {
    if (!("ResizeObserver" in window) || !this.#viewport) return;

    let anchoPrevio = this.#viewport.clientWidth;
    const observar = () => {
      const ancho = this.#viewport?.clientWidth ?? 0;
      if (ancho > 0 && (ancho !== anchoPrevio || !this.#embla)) fn();
      anchoPrevio = ancho;
    };

    const ro = new ResizeObserver(observar);
    ro.observe(this.#viewport);

    const scope = this.closest("[class*='--mobile-only'], [class*='--desktop-only']");
    if (scope instanceof HTMLElement) ro.observe(scope);

    signal.addEventListener("abort", () => ro.disconnect(), { once: true });
  }
  #montarArrastreRaton(signal) {
    const vp = this.#viewport;
    if (!vp) return;

    let activo = false;
    let x0 = 0;
    let scroll0 = 0;

    /** @param {MouseEvent} event */
    const mover = (event) => {
      if (!activo || this.classList.contains("is-enhanced")) return;
      vp.scrollLeft = scroll0 - (event.pageX - x0);
    };

    const soltar = () => {
      activo = false;
      document.removeEventListener("mousemove", mover);
      document.removeEventListener("mouseup", soltar);
    };

    vp.addEventListener(
      "mousedown",
      (event) => {
        if (this.classList.contains("is-enhanced") || event.button !== 0) return;
        activo = true;
        x0 = event.pageX;
        scroll0 = vp.scrollLeft;
        document.addEventListener("mousemove", mover);
        document.addEventListener("mouseup", soltar);
        event.preventDefault();
      },
      { signal },
    );
  }
  /**
   * El gate de intersección decide el motor EN EL MOMENTO de asomarse: con el dedo,
   * el riel ya está listo tal cual (scroller CSS); con puntero fino, monta Embla.
   */
  #armarGate() {
    this.#cancelarGate?.();
    this.#cancelarGate = alVer(this, () => {
      if (rielNativo()) this.#listoNativo();
      else void this.#activar();
    });
  }

  /** Riel nativo listo: bucle si envuelve, sin dots, flechas leídas del scroll, y el chrome destapado. */
  #listoNativo() {
    if (this.#desconectado) return;
    this.#sincronizarBucle();
    this.#construirDots();
    this.#sincronizar();
    this.#marcarListo();
  }

  /** Con el dedo el riel envuelve, salvo que el markup lo apague. */
  #envuelveNativo() {
    return this.getAttribute("data-carousel-loop-touch") !== "false";
  }

  /**
   * Con el dedo todos los rieles envuelven (`#envuelveNativo`). Solo si el juego real
   * desborda; si deja de desbordar (cambio de tamaño), fuera clones.
   */
  #sincronizarBucle() {
    const bucle = this.#bucle;
    if (!bucle || this.#embla) return;
    if (!this.#envuelveNativo() || !rielNativo() || !bucle.desbordaReal()) {
      bucle.quitar();
      return;
    }
    if (bucle.activo()) return;
    const i = bucle.indice();
    if (bucle.poner()) bucle.colocar(i, "instant", true);
  }

  #marcarListo() {
    if (this.classList.contains("carousel--ready")) return;
    this.classList.add("carousel--ready");
    this.dispatchEvent(new CustomEvent("carousel:ready", { bubbles: true }));
  }

  /** @param {boolean} nativo */
  #cambiarMotor(nativo) {
    if (this.#desconectado) return;
    if (nativo) {
      this.#cancelarGate?.();
      this.#desmontar();
      this.#listoNativo();
      return;
    }
    this.#armarGate();
  }

  /** Quita Embla y deja el riel como scroller CSS (`montar()` retira `is-enhanced`). */
  #desmontar() {
    this.#bucle?.quitar();
    this.#montaje?.abort();
    this.#montaje = null;
    this.#embla?.destroy();
    this.#embla = null;
    this.#dots = [];
    if (this.#dotsHost) this.#dotsHost.textContent = "";
  }

  /**
   * Rieles de productos (Relacionados, Viste recientemente, home, look) no
   * envuelven: `data-embla-loop="false"` o el preset `products`. Teselas,
   * anuncios y guía de tallas siguen en bucle si no dicen lo contrario.
   */
  #leerBucle() {
    const desdeHost = opcionesDesde(this);
    return typeof desdeHost.loop === "boolean"
      ? desdeHost.loop
      : this.getAttribute("data-carousel-preset") !== "products";
  }

  /**
   * Recalcula tamaños y posiciones. La llaman `system/product-tabs` y
   * `system/size-guide` cuando un panel que estaba `hidden` —o un `<dialog>` cerrado—
   * se hace visible: mientras lo estaba, el riel medía 0 y Embla dimensionó las
   * celdas sobre esa medida.
   */
  refresh() {
    this.#encolarMedida();

    // SI TODAVÍA NO HAY INSTANCIA, ESTA LLAMADA ES LA QUE LA CREA, y no es un caso raro:
    // un riel dentro de un panel `hidden` —las pestañas del carrusel de producto, la
    // galería de la ficha de tallas dentro de un `<dialog>` cerrado— mide 0x0, así que el
    // `IntersectionObserver` que gobierna la carga perezosa nunca lo ve entrar en pantalla
    // y el gate no dispara. Quien hace visible el panel llama a `refresh()`, y ese es el
    // momento en el que sí tiene sentido montar.
    if (!this.#embla) {
      this.#cancelarGate?.();
      if (rielNativo()) this.#listoNativo();
      else void this.#activar();
      return;
    }

    this.#embla.reInit();
    this.#construirDots();
    this.#sincronizar();
  }

  async #activar() {
    if (this.#activando || this.#embla) return;
    this.#activando = true;
    const [EmblaCarousel, ClassNames] = await Promise.all([cargarEmbla(), cargarClassNames()]);
    // Entre el `import()` y aquí el elemento puede haberse ido: el carrito y la PLP
    // reemplazan secciones enteras por fragmentos. O el puntero pasó a ser un dedo.
    if (this.#desconectado || !this.#track || !this.#viewport || rielNativo()) {
      this.#activando = false;
      if (!this.#desconectado && rielNativo()) this.#listoNativo();
      return;
    }

    const desdeHost = opcionesDesde(this);
    const loop = this.#leerBucle();
    this.#wrap = loop;
    this.#bucle?.quitar();
    this.#montaje = new AbortController();

    this.#embla = montar(EmblaCarousel, this.#viewport, {
      host: this,
      container: this.#track,
      slides: ".carousel__slide",
      plugins: [ClassNames({ snapped: "is-selected", inView: false })],
      signal: this.#montaje.signal,
      rueda: true,
      wrapSiguiente: loop,
      opciones: {
        align: "start",
        skipSnaps: false,
        loop,
        containScroll: loop ? false : "trimSnaps",
        ...desdeHost,
        // EL HIJO SE QUEDA EL GESTO SI HAY HIJO. Con ratón la galería de la card sigue
        // montada dentro del riel (`#sinGaleria()` solo la retira con puntero grueso),
        // y ahí el arrastre sobre la foto es suyo: es lo que prometen sus flechas. Con
        // el dedo no hay galería `is-enhanced` que encontrar, así que el riel se lo
        // queda todo, que es justo lo que se quiere en el teléfono.
        //
        // EL CORAZÓN DE FAVORITOS YA NO SE EXCLUYE. Estuvo excluido —`return false`—
        // desde que `core/embla` marcaba el arrastre en el `pointerdown` y eso le comía
        // el clic a `core/wishlist`. La marca se pone hoy en el primer MOVIMIENTO
        // (`marcarArrastres`), así que un toque limpio sobre el corazón ya no la
        // enciende y el clic pasa, mientras el clic que remata un arrastre sigue
        // ignorándose. Lo que la exclusión costaba era un cuadrado MUERTO de 32×32 en
        // la esquina de cada foto: un swipe que empezara ahí no movía el riel ni un
        // píxel —medido: 0—. En el teléfono las flechas están ocultas y el dedo es el
        // único mando, así que era una de las razones de que el riel «no respondiera».
        watchDrag: (_api, evt) =>
          !(evt.target instanceof Element) ||
          !evt.target.closest("[data-card-gallery].is-enhanced"),
      },
    });

    this.#construirDots();
    this.#embla.on("select", () => {
      this.#sincronizar();
      this.dispatchEvent(new CustomEvent("carousel:select"));
    });
    this.#embla.on("settle", () => {
      this.dispatchEvent(new CustomEvent("carousel:settle"));
    });
    this.#embla.on("reInit", () => {
      this.#construirDots();
      this.#sincronizar();
      this.#encolarMedida();
    });
    this.#sincronizar();

    const slideCount = this.#track?.querySelectorAll(".carousel__slide").length ?? 0;
    const snapCount = this.#embla.scrollSnapList().length;
    if (slideCount > 1 && snapCount <= 1) {
      requestAnimationFrame(() => {
        if (this.#embla) {
          this.#embla.reInit();
          this.#sincronizar();
        }
      });
    }

    this.#activando = false;
    this.#marcarListo();
  }

  /**
   * Click de flecha del RIEL. El disco de 32px se coloca en el mismo canto que el
   * chevron de 24px de la card (primera visible / última visible). Embla transforma
   * la pista, así que un z-index en la card no perfora el overlay de `.carousel__nav`.
   * Si el puntero cae sobre una flecha de galería que sí recibe eventos (card en
   * hover), esa gana; si no, el riel avanza.
   *
   * @param {MouseEvent} e
   * @param {number} dir
   */
  #alClickFlecha(e, dir) {
    if (this.#cederACard(e)) return;
    this.#paso(dir);
  }

  /** @param {MouseEvent} e */
  #cederACard(e) {
    const btn = e.currentTarget;
    if (!(btn instanceof HTMLElement)) return false;
    const previo = btn.style.pointerEvents;
    btn.style.pointerEvents = "none";
    const debajo = document.elementFromPoint(e.clientX, e.clientY);
    btn.style.pointerEvents = previo;
    if (!(debajo instanceof Element)) return false;
    const deCard = debajo.closest("[data-card-gallery-prev], [data-card-gallery-next]");
    if (!(deCard instanceof HTMLElement) || !this.contains(deCard)) return false;
    deCard.click();
    return true;
  }

  /**
   * Un snap adelante o atrás. Con bucle, `scrollNext`/`scrollPrev` toman el
   * atajo; si Embla lo apagó, `#wrap` salta a mano. Sin bucle (productos) el
   * extremo no envuelve: la flecha se apaga en `#sincronizar`.
   *
   * @param {number} dir
   */
  #paso(dir) {
    const api = this.#embla;
    if (!api) {
      this.#pasoNativo(dir);
      return;
    }
    const jump = instante();
    if (dir > 0) {
      if (api.canScrollNext()) api.scrollNext(jump);
      else if (this.#wrap) api.scrollTo(0, true);
      return;
    }
    if (api.canScrollPrev()) api.scrollPrev(jump);
    else if (this.#wrap) api.scrollTo(api.scrollSnapList().length - 1, true);
  }

  /**
   * Un slide adelante o atrás sobre el scroller CSS: se va al PRINCIPIO del siguiente
   * (o anterior) slide, medido contra el borde de contenido del viewport —el mismo
   * punto donde encaja `scroll-snap-align: start` con el `scroll-padding` del
   * sangrado—, así el destino coincide con una parada del snap y no hay ajuste
   * posterior. Con bucle, los clones son slides más y el paso sigue solo; sin bucle,
   * los topes apagan la flecha (`#sincronizarNativo`). El `behavior` lo decide el CSS
   * (`scroll-behavior`), que ya respeta `prefers-reduced-motion`.
   *
   * @param {number} dir
   */
  #pasoNativo(dir) {
    const vp = this.#viewport;
    const track = this.#track;
    if (!vp || !track) return;
    const slides = [...track.querySelectorAll(":scope > .carousel__slide")];
    if (slides.length < 2) return;
    // En el borde del colchón de clones: recolocar primero y volver a intentar.
    if (this.#bucle?.activo() && this.#bucle.recolocar()) {
      this.#pasoNativo(dir);
      return;
    }
    const max = vp.scrollWidth - vp.clientWidth;
    const css = getComputedStyle(vp);
    const relleno = Number.parseFloat(css.scrollPaddingLeft) || Number.parseFloat(css.paddingLeft) || 0;
    const borde = vp.getBoundingClientRect().left + relleno;
    const paradas = slides.map((s) => vp.scrollLeft + s.getBoundingClientRect().left - borde);
    const destino =
      dir > 0
        ? paradas.find((p) => p > vp.scrollLeft + 1)
        : paradas.reverse().find((p) => p < vp.scrollLeft - 1);
    if (destino === undefined) return;
    vp.scrollTo({ left: Math.min(Math.max(destino, 0), max) });
  }

  #sincronizar() {
    const api = this.#embla;
    if (!api) {
      this.#sincronizarNativo();
      return;
    }

    const hayRecorrido = api.scrollSnapList().length > 1;
    for (const nav of this.querySelectorAll(".carousel__nav, .cross-sell-carousel__nav")) {
      nav.toggleAttribute("hidden", !hayRecorrido);
    }

    this.#marcarInactivo(this.#prev, !api.canScrollPrev());
    this.#marcarInactivo(this.#next, !api.canScrollNext());
    this.#sincronizarDots();
  }

  /** Flechas y nav leídos del scroller: sin recorrido no hay nav; sin bucle, los topes apagan. */
  #sincronizarNativo() {
    const vp = this.#viewport;
    if (!vp) return;
    const max = vp.scrollWidth - vp.clientWidth;
    // El recorrido se mide sobre los slides REALES: con clones el scroller siempre desborda.
    const hayRecorrido = this.#bucle ? this.#bucle.desbordaReal() : max > 2;
    for (const nav of this.querySelectorAll(".carousel__nav, .cross-sell-carousel__nav")) {
      nav.toggleAttribute("hidden", !hayRecorrido);
    }
    const envuelve = this.#bucle?.activo() ?? false;
    this.#marcarInactivo(this.#prev, !hayRecorrido || (!envuelve && vp.scrollLeft <= 1));
    this.#marcarInactivo(this.#next, !hayRecorrido || (!envuelve && vp.scrollLeft >= max - 1));
  }

  /** @param {HTMLElement | null} btn @param {boolean} off */
  #marcarInactivo(btn, off) {
    if (!btn) return;
    btn.setAttribute("aria-disabled", String(off));
    btn.classList.toggle("carousel__control--disabled", off);
  }

  /**
   * Los puntos se construyen sobre los snaps, no sobre las celdas: con
   * `slidesToScroll: auto` una parada agrupa varias tarjetas.
   */
  #construirDots() {
    const host = this.#dotsHost;
    const api = this.#embla;
    if (!host) return;
    // Sin Embla no hay índice de snap: no se pintan puntos (con el dedo el gesto y
    // el recorte de la siguiente tarjeta ya dicen que hay más).
    const snaps = api ? api.scrollSnapList() : [];
    if (snaps.length < 2) {
      host.textContent = "";
      this.#dots = [];
      return;
    }

    const plantilla = host.dataset.dotLabel ?? "";
    host.textContent = "";
    this.#dots = snaps.map((_snap, i) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "carousel__dot";
      dot.setAttribute("aria-label", plantilla.replace("[index]", String(i + 1)));
      dot.addEventListener("click", () => api.scrollTo(i, instante()));
      host.appendChild(dot);
      return dot;
    });
  }

  #sincronizarDots() {
    if (!this.#dots.length || !this.#embla) return;
    const activo = this.#embla.selectedScrollSnap();
    this.#dots.forEach((dot, i) => {
      const on = i === activo;
      dot.classList.toggle("is-active", on);
      dot.setAttribute("aria-current", on ? "true" : "false");
    });
  }

  #encolarMedida() {
    if (this.#rafMedida) return;
    this.#rafMedida = requestAnimationFrame(() => {
      this.#rafMedida = 0;
      this.#medirFoto();
    });
  }

  /**
   * ALTO DE LA FOTO DE LA TARJETA, para que las flechas se centren en ELLA y no en el
   * slide entero.
   *
   * Centradas en el viewport quedaban visiblemente bajas: el slide es la foto (4:5) MÁS
   * el bloque de marca, título y precio, así que su mitad cae dentro del texto. Y no se
   * puede calcular en CSS: el alto de la foto depende del ancho del slide, que lo decide
   * el preset y el punto de ruptura. Se mide y se publica como custom property; el CSS
   * hace el resto.
   */
  #medirFoto() {
    const media = this.#track?.querySelector(".card__media, .card__media-link");
    if (!(media instanceof HTMLElement)) return;
    const alto = media.getBoundingClientRect().height;
    if (alto > 0) this.style.setProperty("--carousel-media-h", `${Math.round(alto)}px`);
  }
}

if (!customElements.get("enova-carousel")) {
  customElements.define("enova-carousel", EnovaCarousel);
}

export { EnovaCarousel };
